import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { orderDetailDispatchers } from 'reduxModules/users/dispatchers'
import _ from 'lodash'
import classnames from 'classnames'
import math from 'reduxModules/utils/math'
import utils from '../../utils/cartItemUtils'
import AddressDetails from '../AddressDetails'
import PaymentMethodDetails from '../PaymentMethodDetails'
import './styles.css'

// the API sends money as "0.00" strings or as numbers, depending on the
// order; a bare numeric 0 would otherwise print itself as a stray "0"
const nonZero = (amount) => Number(amount) !== 0 && !Number.isNaN(Number(amount))
// an old order whose transaction is gone from Braintree has no totals at all
const money = (amount) => (amount == null ? '—' : `$${math.toFixed(amount)}`)

export class OrderDetail extends Component {
  static getSubtotal(items) {
    return math.toFixed(
      parseFloat(
        items.reduce((acc, item) => acc + item.regularPrice * item.quantity, 0)
      )
    )
  }

  componentDidMount() {
    const { isLoaded, loadOrderDetail, id } = this.props
    if (!isLoaded) loadOrderDetail(id)
  }

  render() {
    const {
      subscriptionTransaction: subOrder,
      items,
      isMobile,
      width,
      showFullDetail,
      shippingAddress,
      creditCard,
      paypalAccount,
      creditCardDetails,
      paypalDetails,
      billingAddress,
      discounted,
      itemsCost,
      shippingCost,
      taxAmount,
      taxPctg,
      cost,
      amount,
      subscription
    } = this.props

    return (
      <div className="order-description">
        {!isMobile && (
          <div className="items-header">
            <div className="item-name">items</div>
            {!subOrder && <div className="quantity">{width < 980 ? 'qt' : 'quantity'}</div>}
            <div className="item-price">price</div>
          </div>
        )}
        <div className="items">
          {!subOrder && items.map((item) => (
            <div className="item" key={item.id}>
              <div className="item-name">
                {item.ordered ? (
                  <>
                    <div className="type blue">
                      <span>{utils.getNaturalType(item.orderedModel, item.ordered)}</span>
                    </div>
                    <div className="title">
                      <span>{utils.getTitle(item.orderedModel, item.ordered)}</span>
                    </div>
                  </>
                ) : (
                  // an old order whose product was deleted comes back as a
                  // placeholder item: no product, no price
                  <div className="title">
                    <span>Item no longer available</span>
                  </div>
                )}
              </div>
              <div className="quantity">{item.quantity}</div>
              <div className="item-price">
                {item.price == null ? '—' : `$${math.toFixed(item.price * item.quantity)}`}
              </div>
            </div>
          ))}
          {subOrder && (
            <div className="item">
              <div className="item-name">
                <div className="type blue">
                  <span>Subscription</span>
                </div>
                {_.has(subscription, 'plan') && (
                  <div className="title">
                    <span>Subscription renewal</span>
                  </div>
                )}
              </div>
              <div className="item-price">${math.toFixed(amount)}</div>
            </div>
          )}
        </div>
        <div className={classnames('bottom-section', { simplified: !showFullDetail })}>
          {showFullDetail && (
            <div className="full-details">
              {shippingAddress && (
                <div className="shipping-info">
                  <div className="small-header">
                    <span>shipped to</span>
                  </div>
                  <AddressDetails {...shippingAddress} />
                </div>
              )}
              {(_.isObject(creditCard) || _.isObject(paypalAccount)) && (
                <div className={classnames('payment-info', { 'only-payment-method': !shippingAddress })}>
                  <div className="small-header">
                    <span>payment method</span>
                  </div>
                  <PaymentMethodDetails creditCardPayment={creditCard} paypalPayment={paypalAccount} />
                </div>
              )}
              {subOrder && (
                <div className="payment-info">
                  <div className="small-header">
                    <span>payment method</span>
                  </div>
                  <PaymentMethodDetails creditCardPayment={creditCardDetails} paypalPayment={paypalDetails} />
                </div>
              )}
              {billingAddress && billingAddress.firstName && (
                <div className="billing-info">
                  <div className="small-header">
                    <span>billed to</span>
                  </div>
                  <AddressDetails {...billingAddress} />
                </div>
              )}
            </div>
          )}
          {!subOrder && (
            <div className="order-details">
              <div className="small-header">order details</div>
              {!_.isEmpty(items) && (
                <div className="order-details-container">
                  <div className="order-details-items">
                    {nonZero(discounted) && <div>Discount</div>}
                    {itemsCost != null && <div>Items total:</div>}
                    {nonZero(shippingCost) && <div>Shipping:</div>}
                    {(nonZero(taxAmount) || nonZero(taxPctg)) && <div>Taxes:</div>}
                  </div>
                  <div className="order-details-values">
                    {nonZero(discounted) && <div>-${math.toFixed(Math.abs(discounted))}</div>}
                    {itemsCost != null && <div>${math.toFixed(itemsCost)}</div>}
                    {nonZero(shippingCost) && <div>${math.toFixed(shippingCost)}</div>}
                    <div className="taxes-amount">
                      {nonZero(taxPctg) && <div>%{taxPctg}</div>}
                      {nonZero(taxAmount) && <div className="tax-divider">-</div>}
                      {nonZero(taxAmount) && <div>${math.toFixed(taxAmount)}</div>}
                    </div>
                  </div>
                </div>
              )}
              <div className="order-total">
                <div>order total:</div>
                <div>{money(subOrder ? amount : cost)}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
}

OrderDetail.propTypes = {
  isLoaded: PropTypes.bool,
  loadOrderDetail: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  subscriptionTransaction: PropTypes.bool,
  isMobile: PropTypes.bool,
  width: PropTypes.number,
  showFullDetail: PropTypes.bool,
  shippingAddress: PropTypes.object,
  creditCard: PropTypes.object,
  paypalAccount: PropTypes.object,
  creditCardDetails: PropTypes.object,
  paypalDetails: PropTypes.object,
  billingAddress: PropTypes.object,
  discounted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  itemsCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shippingCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  taxAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  taxPctg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subscription: PropTypes.object
}

OrderDetail.defaultProps = {
  items: [],
  id: '',
  isLoaded: false,
  subscriptionTransaction: false,
  isMobile: false,
  width: 0,
  showFullDetail: false,
  shippingAddress: null,
  creditCard: null,
  paypalAccount: null,
  creditCardDetails: null,
  paypalDetails: null,
  billingAddress: null,
  discounted: '0.00',
  itemsCost: '0.00',
  shippingCost: '0.00',
  taxAmount: '0.00',
  taxPctg: '0.00',
  cost: '0.00',
  amount: '0.00',
  subscription: null
}

export default connect(
  null,
  orderDetailDispatchers
)(OrderDetail)
